import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  TextContainer,
  Text,
  Banner,
  LegacyStack,
  Spinner,
  InlineError,
  Badge,
  ChoiceList,
  Icon,
  Tooltip,
  Checkbox
} from '@shopify/polaris';
import { SaveMinor } from '@shopify/polaris-icons';
import { useAuth } from '../../contexts/AuthContext';
// Import the sizechart service functions we need
import { generateSizechartWithAI } from '../../services/sizechartAIService';
import { getDefaultAIModel, getAvailableAIModels } from '../../services/aiModelService';
import { hasEnoughCreditsForAIModel } from '../../services/creditService';
import { deductCredits } from '../../services/optimizedCreditService';
import { saveUserPreference, getUserPreferences } from '../../services/userPreferenceService';
// Import garment template services
import { 
  garmentTypes, 
  fitTypes, 
  stretchLevels, 
  analyzeStretchLevel, 
  analyzeFitType 
} from '../../services/garmentTemplateService';
// Import custom components
import FitGuideVisualizer from '../../components/sizecharts/FitGuideVisualizer';
import MeasurementDiagram from '../../components/sizecharts/MeasurementDiagram';
// Import Firestore for saving generated charts
import { db } from '../../firebase';
import { doc, collection, addDoc, query, where, orderBy, limit, getDocs, serverTimestamp } from 'firebase/firestore';

export default function ProductChartGenerator({
  shopId,
  userId,
  settings,
  refreshData
}) {
  // State for form fields
  const [chartName, setChartName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [responseLanguage, setResponseLanguage] = useState('English');
  const [inputType, setInputType] = useState('text'); // 'text' or 'image'
  const [inputText, setInputText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [includeBaseSizes, setIncludeBaseSizes] = useState(true); // Option to include base sizes from input
  
  // State for garment template
  const [garmentType, setGarmentType] = useState('dress');
  const [materialInfo, setMaterialInfo] = useState('');
  const [showMeasurementDiagram, setShowMeasurementDiagram] = useState(true);
  // These will be determined by AI, not set by user
  const [fitType, setFitType] = useState(null);
  const [stretchLevel, setStretchLevel] = useState(null);
  
  // State for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [creditCost, setCreditCost] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  
  // State for templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  
  // State for AI model
  const [aiModels, setAiModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [savedSettings, setSavedSettings] = useState(null);
  
  // Auth context
  const { user } = useAuth();

  // Track if this is the first render
  const isFirstRender = React.useRef(true);
  
  // Load available countries from settings - ONLY on first render
  React.useEffect(() => {
    // Only run this effect once on component mount
    if (isFirstRender.current && settings) {
      console.log('FIRST RENDER: Loading countries from settings');
      isFirstRender.current = false;
      
      // Check if countries exist in settings and is a non-empty array
      if (settings.countries && Array.isArray(settings.countries) && settings.countries.length > 0) {
        // Transform the countries array into options format
        const countries = settings.countries.map(countryCode => ({
          label: countryCode,
          value: countryCode
        }));
        console.log('Setting available countries:', countries.map(c => c.value).join(', '));
        setAvailableCountries(countries);
        
        // DO NOT set any default countries - let the user explicitly select them
        console.log('No default countries set - user must explicitly select countries');
      } else {
        // If no countries in settings, set an empty array
        console.log('No countries found in settings, setting empty array');
        setAvailableCountries([]);
      }
    }
  }, [settings]); // Only depend on settings
  
  // Handle country selection change
  const handleCountryChange = useCallback((selected) => {
    console.log('Country selection changed to:', selected);
    
    // Ensure we have a valid array of selections
    if (!Array.isArray(selected)) {
      console.warn('Selected is not an array, using empty array instead');
      setSelectedCountries([]);
      return;
    }
    
    // Create a completely new array to avoid any reference issues
    const exactSelection = [...selected];
    console.log('Exact selection from user:', exactSelection);
    
    // Set the state with exactly what the user selected
    setSelectedCountries(exactSelection);
  }, []); // No dependencies - this function never needs to change
  
  // Load saved settings from Firebase
  const loadSavedSettings = async () => {
    try {
      console.log(`Loading saved settings for shop ${shopId}, user ${userId}`);
      const preferences = await getUserPreferences(shopId, userId, 'productChartGenerator');
      
      if (preferences) {
        console.log('Loaded saved preferences:', preferences);
        setSavedSettings(preferences);
        
        // Apply non-model settings immediately
        if (preferences.chartName) {
          console.log(`Setting chart name to ${preferences.chartName}`);
          setChartName(preferences.chartName);
        }
        
        if (preferences.inputType) {
          console.log(`Setting input type to ${preferences.inputType}`);
          setInputType(preferences.inputType);
        }
        
        if (preferences.selectedCountries && Array.isArray(preferences.selectedCountries)) {
          // IMPORTANT: Only use the exact countries from preferences, don't add defaults
          // Filter out any 'US' that might have been saved accidentally unless explicitly selected
          const hasExplicitUS = preferences.explicitlySelectedUS === true;
          const filteredCountries = hasExplicitUS ? 
            preferences.selectedCountries : 
            preferences.selectedCountries.filter(country => country !== 'US');
            
          console.log(`Setting selected countries to ${filteredCountries.join(', ')} (filtered from ${preferences.selectedCountries.join(', ')})`);
          console.log(`US explicitly selected: ${hasExplicitUS}`);
          
          // Only set countries if there are any after filtering
          if (filteredCountries.length > 0) {
            setSelectedCountries(filteredCountries);
          }
        }
        
        if (preferences.responseLanguage) {
          console.log(`Setting response language to ${preferences.responseLanguage}`);
          setResponseLanguage(preferences.responseLanguage);
        }
        
        // Apply garment template settings
        if (preferences.garmentType) {
          console.log(`Setting garment type to ${preferences.garmentType}`);
          setGarmentType(preferences.garmentType);
        }
        
        if (preferences.fitType) {
          console.log(`Setting fit type to ${preferences.fitType}`);
          setFitType(preferences.fitType);
        }
        
        if (preferences.stretchLevel) {
          console.log(`Setting stretch level to ${preferences.stretchLevel}`);
          setStretchLevel(preferences.stretchLevel);
        }
        
        if (preferences.showMeasurementDiagram !== undefined) {
          console.log(`Setting show measurement diagram to ${preferences.showMeasurementDiagram}`);
          setShowMeasurementDiagram(preferences.showMeasurementDiagram);
        }
        
        if (preferences.includeBaseSizes !== undefined) {
          console.log(`Setting includeBaseSizes to ${preferences.includeBaseSizes}`);
          setIncludeBaseSizes(preferences.includeBaseSizes);
        }
        
        // Apply template selection if available
        if (preferences.selectedTemplateId) {
          console.log(`Setting selected template ID to ${preferences.selectedTemplateId}`);
          setSelectedTemplateId(preferences.selectedTemplateId);
          
          // Note: The actual template object will be set when templates are loaded
          // We'll handle this in a separate useEffect that depends on templates and selectedTemplateId
        }
        
        if (preferences.selectedModelId) {
          console.log(`Saved model ID found: ${preferences.selectedModelId}`);
          console.log('Model settings will be applied in useEffect after models are loaded');
        }
      } else {
        console.log('No saved preferences found');
      }
    } catch (error) {
      console.error('Error loading saved settings:', error);
    }
  };
  
  // Update selected model when ID changes
  useEffect(() => {
    if (selectedModelId && aiModels.length > 0) {
      const model = aiModels.find(m => m.id === selectedModelId);
      setSelectedModel(model || null);
      setCreditCost(model?.creditCostPerOutput || 1);
    }
  }, [selectedModelId, aiModels]);
  
  // Update selected template when templates are loaded or template ID changes
  useEffect(() => {
    if (selectedTemplateId && templates.length > 0) {
      console.log('Looking for template with ID:', selectedTemplateId);
      const template = templates.find(t => t.id === selectedTemplateId);
      
      if (template) {
        console.log('Found template:', template);
        setSelectedTemplate(template);
        
        // Apply template settings
        if (template.garmentType) {
          setGarmentType(template.garmentType);
        }
        
        if (template.fitType) {
          setFitType(template.fitType);
        }
        
        if (template.stretchLevel) {
          setStretchLevel(template.stretchLevel);
        }
        
        if (template.materialInfo) {
          setMaterialInfo(template.materialInfo);
        }
        
        if (template.showMeasurementDiagram !== undefined) {
          setShowMeasurementDiagram(template.showMeasurementDiagram);
        }
      } else {
        console.log('Template not found with ID:', selectedTemplateId);
        setSelectedTemplate(null);
      }
    }
  }, [selectedTemplateId, templates]);

  // Load AI models on component mount
  useEffect(() => {
    const loadAIModelsWithSettings = async () => {
      try {
        const models = await getAvailableAIModels();
        setAiModels(models);
        
        // If we have saved settings with a model ID, try to find that model
        if (savedSettings && savedSettings.selectedModelId && savedSettings.selectedModelType) {
          console.log(`Looking for saved model ID: ${savedSettings.selectedModelId} of type ${savedSettings.selectedModelType}`);
          
          // Find models matching the saved type
          const typeMatches = models.filter(model => 
            (model.type || 'text') === savedSettings.selectedModelType
          );
          
          // Try to find the exact model ID match first
          const exactMatch = typeMatches.find(model => model.modelId === savedSettings.selectedModelId);
          
          if (exactMatch) {
            console.log(`Found exact match for saved model: ${exactMatch.displayName}`);
            setSelectedModelId(exactMatch.id);
            setSelectedModel(exactMatch);
            setCreditCost(exactMatch.creditCostPerOutput || 1);
          } else if (typeMatches.length > 0) {
            // If no exact match, use the first model of the correct type
            console.log(`No exact match found, using first ${savedSettings.selectedModelType} model: ${typeMatches[0].displayName}`);
            setSelectedModelId(typeMatches[0].id);
            setSelectedModel(typeMatches[0]);
            setCreditCost(typeMatches[0].creditCostPerOutput || 1);
          } else {
            // Fall back to default model
            await setDefaultModel(models);
          }
        } else {
          // No saved settings, use default model
          await setDefaultModel(models);
        }
      } catch (error) {
        console.error('Error loading AI models:', error);
        setError('Failed to load AI models. Please try again.');
      }
    };
    
    const setDefaultModel = async (models) => {
      if (models.length > 0) {
        const defaultModel = await getDefaultAIModel();
        if (defaultModel) {
          setSelectedModelId(defaultModel.id);
          setSelectedModel(defaultModel);
          setCreditCost(defaultModel.creditCostPerOutput || 1);
        } else {
          // If no default, use the first model
          setSelectedModelId(models[0].id);
          setSelectedModel(models[0]);
          setCreditCost(models[0].creditCostPerOutput || 1);
        }
      }
    };

    loadAIModelsWithSettings();
  }, [savedSettings]);

  // Load templates from Firestore
  const loadTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const templatesRef = collection(db, `shops/${shopId}/sizechart_templates`);
      const q = query(templatesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const templatesData = [];
      querySnapshot.forEach((doc) => {
        templatesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('Loaded templates:', templatesData);
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load templates. Please try again.');
    } finally {
      setIsLoadingTemplates(false);
    }
  };
  
  // Handle template selection change
  const handleTemplateChange = useCallback((templateId) => {
    console.log('Template selection changed to:', templateId);
    setSelectedTemplateId(templateId);
    
    // Find the selected template
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template || null);
    
    // Apply template settings if a template is selected
    if (template) {
      console.log('Applying template settings:', template);
      
      // Apply garment template settings from the selected template
      if (template.garmentType) {
        setGarmentType(template.garmentType);
      }
      
      if (template.fitType) {
        setFitType(template.fitType);
      }
      
      if (template.stretchLevel) {
        setStretchLevel(template.stretchLevel);
      }
      
      if (template.materialInfo) {
        setMaterialInfo(template.materialInfo);
      }
      
      if (template.showMeasurementDiagram !== undefined) {
        setShowMeasurementDiagram(template.showMeasurementDiagram);
      }
    }
  }, [templates]);
  
  // Load saved settings and templates on component mount
  useEffect(() => {
    loadSavedSettings();
    loadTemplates();
  }, []);
  
  // Save current settings to Firebase
  const saveCurrentSettings = async () => {
    try {
      setIsSavingSettings(true);
      
      // If no model is selected, try to use a default one
      if (!selectedModelId) {
        console.warn('No model selected, using default model');
        // Use the current model if available, otherwise use the first compatible model
        const compatibleModels = aiModels.filter(model => {
          if (inputType === 'text') {
            return !model.type || model.type === 'text';
          } else { // image
            return model.type === 'vision';
          }
        });
        
        if (compatibleModels.length > 0) {
          const defaultModel = compatibleModels[0];
          console.log('Using default model for saving:', defaultModel.displayName);
          setSelectedModelId(defaultModel.id);
        }
      }
      
      // Get the current selected model
      const currentModel = selectedModel || aiModels.find(m => m.id === selectedModelId);
      
      // Log current state before saving
      console.log('Current state before saving:', {
        shopId,
        userId,
        inputType,
        selectedCountries,
        responseLanguage,
        selectedModelId,
        selectedModel: currentModel ? currentModel.displayName : null,
        modelId: currentModel ? currentModel.modelId : null  // The actual provider model ID
      });
      
      // Check if US is explicitly selected by checking the DOM
      const usCheckbox = document.querySelector('input[value="US"]:checked');
      const isUSExplicitlySelected = usCheckbox !== null;
      console.log('Is US explicitly selected in UI?', isUSExplicitlySelected);
      
      // Prepare settings object
      const settings = {
        chartName,
        inputType,
        selectedCountries,
        // Track whether US was explicitly selected by the user
        explicitlySelectedUS: isUSExplicitlySelected,
        responseLanguage,
        selectedModelId: currentModel ? currentModel.modelId : null,
        selectedModelType: currentModel ? (currentModel.type || 'text') : null,
        includeBaseSizes,
        // Template selection
        selectedTemplateId,
        // Garment template settings
        garmentType,
        fitType,
        stretchLevel,
        showMeasurementDiagram,
        materialInfo,
        lastUpdated: new Date().toISOString()
      };
      
      console.log('Saving settings:', settings);
      
      // Save to Firebase
      await saveUserPreference(shopId, userId, 'productChartGenerator', settings);
      
      // Update saved settings state
      setSavedSettings(settings);
      
      // Show success message
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('There was an error saving your preferences. Please try again.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate required fields based on input type
    if (!productCategory) {
      setError('Please enter a product category');
      return;
    }
    
    if (inputType === 'text' && !inputText) {
      setError('Please enter product details');
      return;
    }
    
    if (inputType === 'image' && !imageUrl) {
      setError('Please enter an image URL');
      return;
    }
    
    if (selectedCountries.length === 0) {
      setError('Please select at least one target market');
      return;
    }
    
    if (!selectedModelId) {
      setError('Please select an AI model');
      return;
    }
    
    // Clear any previous errors
    setError(null);
    setResult(null);
    setLoading(true);
    
    try {
      // Calculate total credit cost based on number of countries
      const totalCreditCost = (creditCost || 1) * selectedCountries.length;
      
      // Check if user has enough credits
      const hasCredits = await hasEnoughCreditsForAIModel(userId, totalCreditCost);
      
      if (!hasCredits) {
        setError(`You don't have enough credits (${totalCreditCost} required). Please purchase more credits or use a different model.`);
        setLoading(false);
        return;
      }
      
      // Process each country sequentially
      const results = [];
      
      // EMERGENCY FIX: Completely bypass the selectedCountries state
      // Instead, directly check which checkboxes are checked in the UI
      const countriesToProcess = [];
      
      // Get all checked country checkboxes from the UI
      const checkboxes = document.querySelectorAll('input[type="checkbox"]:checked');
      checkboxes.forEach(checkbox => {
        // Get the country value from the checkbox or its parent
        const value = checkbox.value || checkbox.closest('[data-value]')?.getAttribute('data-value');
        if (value && !value.includes('Select all') && !countriesToProcess.includes(value)) {
          countriesToProcess.push(value);
        }
      });
      
      // If we still have no countries (unlikely), fall back to what's in the state
      // but filter out 'US' if it wasn't explicitly selected
      if (countriesToProcess.length === 0) {
        const filteredCountries = selectedCountries.filter(country => {
          // Only include 'US' if it's explicitly checked in the UI
          if (country === 'US') {
            return document.querySelector('input[value="US"]:checked') !== null;
          }
          return true;
        });
        
        // Use the filtered countries, or just the first non-US country if available
        if (filteredCountries.length > 0) {
          countriesToProcess.push(...filteredCountries);
        } else if (selectedCountries.length > 0 && selectedCountries[0] !== 'US') {
          countriesToProcess.push(selectedCountries[0]);
        }
      }
      
      console.log('Countries to process (EMERGENCY FIX):', countriesToProcess);
      
      for (const country of countriesToProcess) {
        console.log(`Generating size chart for ${country}...`);
        
        // Prepare the input data based on input type
        const inputData = inputType === 'text' ? inputText : imageUrl;
        
        // Generate the size chart - pass only garmentType and materialInfo, let AI determine fit and stretch
        // Create a modified input that includes the chart name if provided
        const modifiedInput = chartName ? 
          `Chart Name: ${chartName}

${inputData}` : 
          inputData;
          
        console.log('Generating chart with name:', chartName || 'No name provided');
          
        const result = await generateSizechartWithAI(
          userId,
          shopId,
          productCategory,
          country,
          responseLanguage,
          modifiedInput,
          selectedModelId,
          includeBaseSizes,
          garmentType,
          null, // Let AI determine fit type
          null, // Let AI determine stretch level
          materialInfo
        );
        
        // Extract fit type and stretch level from AI response if available
        // The AI might return fit_type and stretch_level directly, or embed them in the response
        if (result.fit_type) {
          setFitType(result.fit_type);
          console.log(`Using AI-generated fit_type: ${result.fit_type}`);
        } else if (result.fit_info && result.fit_info.length > 0) {
          // Fall back to extracting from fit_info if top-level fit_type is not available
          const fitTags = result.fit_info.map(item => item.fit_tag);
          
          if (fitTags.length > 0) {
            // Find the most common fit tag
            const fitTagCounts = {};
            let mostCommonFitTag = null;
            let maxCount = 0;
            
            fitTags.forEach(tag => {
              if (!tag) return;
              
              if (!fitTagCounts[tag]) {
                fitTagCounts[tag] = 1;
              } else {
                fitTagCounts[tag]++;
              }
              
              if (fitTagCounts[tag] > maxCount) {
                maxCount = fitTagCounts[tag];
                mostCommonFitTag = tag;
              }
            });
            
            if (mostCommonFitTag) {
              // Convert fit tag to fit type (e.g., 'Regular Fit' -> 'regular')
              const fitType = mostCommonFitTag.toLowerCase().replace(' fit', '').trim();
              setFitType(fitType);
              console.log(`Extracted fit type from fit_info as fallback: ${fitType}`);
            }
          }
        }
        
        // Extract stretch level - prioritize top-level stretch_level from AI
        if (result.stretch_level) {
          setStretchLevel(result.stretch_level);
          console.log(`Using AI-generated stretch_level: ${result.stretch_level}`);
        } else if (materialInfo) {
          // If AI didn't determine stretch level, use our material analysis
          const analyzedStretchLevel = analyzeStretchLevel(materialInfo);
          setStretchLevel(analyzedStretchLevel);
          console.log(`Using analyzed stretch level from material info as fallback: ${analyzedStretchLevel}`);
        }
        
        // Log the result for debugging
        console.log('===== AI GENERATED SIZE CHART RESULT =====');
        console.log('Result structure:', JSON.stringify(result, null, 2));
        console.log('Product Category:', productCategory);
        console.log('Country:', country);
        console.log('Response Language:', responseLanguage);
        
        // Add the country to the result
        results.push({
          country,
          ...result
        });
      }
      
      // Note: We don't need to deduct credits here as the generateSizechartWithAI function
      // already handles credit deduction internally for each country
      
      // Set the results
      setResult(results);
      
      // Save the generated charts to Firestore for the SizeChartDesignerNew component
      try {
        for (const result of results) {
          // Get template settings if available
          const templatesRef = collection(db, `shops/${shopId}/sizechart_templates`);
          const q = query(templatesRef, orderBy('createdAt', 'desc'), limit(1));
          const querySnapshot = await getDocs(q);
          
          let templateSettings = null;
          if (!querySnapshot.empty) {
            templateSettings = {
              id: querySnapshot.docs[0].id,
              ...querySnapshot.docs[0].data()
            };
          }
          
          // Save the chart to Firestore
          // First, ensure the result has a valid structure
          let processedResult = result;
          
          // Log the chart name and result structure for debugging
          console.log('Chart name from user input:', chartName);
          console.log('Selected template:', selectedTemplate ? selectedTemplate.name : 'None');
          console.log('Result structure before processing:', JSON.stringify(result));
          
          // Get the country value to use in the chart
          const countryValue = result.country || (selectedCountries && selectedCountries.length > 0 ? selectedCountries[0] : 'Global');
          
          // Determine the unit from the result or use a default
          const unitValue = result.unit || 
            (result.sizechart?.columns?.some(col => col.unit === 'cm') ? 'cm' : 
             result.columns?.some(col => col.unit === 'cm') ? 'cm' : 'in');
          
          // Check if the result has a nested sizechart structure or a flat structure
          if (result.sizechart && typeof result.sizechart === 'object') {
            // The result has a nested structure - preserve all data
            processedResult = {
              summary: `Generated sizechart for ${productCategory} in ${countryValue}`,
              unit: unitValue,
              country: countryValue,
              sizechart: {
                // IMPORTANT: Always prioritize the user-provided chart name
                name: chartName || `${countryValue} ${productCategory} Size Chart`,
                category: result.sizechart.category || productCategory,
                // Preserve all the original data from the nested structure
                columns: result.sizechart.columns || [],
                rows: result.sizechart.rows || [],
                notes: result.sizechart.notes || [],
                fit_info: result.sizechart.fit_info || [],
                // Preserve any other fields that might be in the original result
                ...result.sizechart
              }
            };
          } else {
            // The result has a flat structure - create a nested structure
            processedResult = {
              summary: `Generated sizechart for ${productCategory} in ${countryValue}`,
              unit: unitValue,
              country: countryValue,
              sizechart: {
                // IMPORTANT: Always prioritize the user-provided chart name
                name: chartName || `${countryValue} ${productCategory} Size Chart`,
                category: result.category || productCategory,
                // Use the data from the flat structure
                columns: result.columns || [],
                rows: result.rows || [],
                notes: result.notes || [],
                fit_info: result.fit_info || [],
                // Add any countries from the result
                countries: result.countries || [countryValue]
              }
            };
          }
          
          // Log the processed structure for debugging
          console.log('Processed sizechart structure:', JSON.stringify(processedResult.sizechart));
          
          // Log the processed result
          console.log('Result structure after processing:', JSON.stringify(processedResult));
          
          // Clean up the processed result to ensure it's Firestore-compatible
          // Remove any undefined or function values that could cause Firestore errors
          const cleanProcessedResult = JSON.parse(JSON.stringify(processedResult));
          
          // Extract fit type from fit_info if available
          let extractedFitType = null;
          if (cleanProcessedResult.sizechart && cleanProcessedResult.sizechart.fit_info && cleanProcessedResult.sizechart.fit_info.length > 0) {
            // Count occurrences of each fit tag
            const fitTagCounts = {};
            cleanProcessedResult.sizechart.fit_info.forEach(item => {
              if (item.fit_tag) {
                const tag = item.fit_tag.toLowerCase();
                fitTagCounts[tag] = (fitTagCounts[tag] || 0) + 1;
              }
            });
            
            // Find the most common fit tag
            let mostCommonTag = null;
            let highestCount = 0;
            
            Object.entries(fitTagCounts).forEach(([tag, count]) => {
              if (count > highestCount) {
                mostCommonTag = tag;
                highestCount = count;
              }
            });
            
            // Map the fit tag to a fit type
            if (mostCommonTag) {
              if (mostCommonTag.includes('slim') || mostCommonTag.includes('fitted') || mostCommonTag.includes('tight')) {
                extractedFitType = 'slim';
              } else if (mostCommonTag.includes('oversized')) {
                extractedFitType = 'oversized';
              } else if (mostCommonTag.includes('loose') || mostCommonTag.includes('relaxed')) {
                extractedFitType = 'loose';
              } else if (mostCommonTag.includes('regular')) {
                extractedFitType = 'regular';
              }
            }
          }
          
          // Extract stretch level from material info if not already set
          let extractedStretchLevel = stretchLevel;
          if (!extractedStretchLevel && materialInfo) {
            extractedStretchLevel = analyzeStretchLevel(materialInfo);
          }
          
          // IMPORTANT: Make sure we're using the original sizechart data, not the processed version
          // that might have lost information during JSON.stringify/parse
          const sizechartToSave = {
            ...cleanProcessedResult.sizechart,
            // Ensure these critical properties are preserved
            name: chartName || cleanProcessedResult.sizechart.name || `${productCategory} Size Chart`,
            category: cleanProcessedResult.sizechart.category || productCategory,
            columns: cleanProcessedResult.sizechart.columns || [],
            rows: cleanProcessedResult.sizechart.rows || [],
            notes: cleanProcessedResult.sizechart.notes || [],
            fit_info: cleanProcessedResult.sizechart.fit_info || [],
            countries: cleanProcessedResult.sizechart.countries || [cleanProcessedResult.country || selectedCountries[0]]
          };
          
          // Log the final sizechart being saved
          console.log('Final sizechart being saved to Firebase:', JSON.stringify(sizechartToSave));
          
          const chartData = {
            // Use the properly preserved sizechart data
            sizechart: sizechartToSave,
            // Include all the metadata
            productCategory,
            inputType,
            responseLanguage,
            createdAt: serverTimestamp(),
            createdBy: userId,
            modelId: selectedModelId,
            // Use the selected template if available, otherwise use the template from settings
            templateId: selectedTemplateId || templateSettings?.id || null,
            // Include template settings if they exist
            ...(selectedTemplate ? { templateSettings: selectedTemplate } : 
               templateSettings ? { templateSettings } : {}),
            // Add garment template information
            garmentType,
            // Use AI-generated values first, then fall back to extracted values, then defaults
            fitType: cleanProcessedResult.sizechart.fit_type || extractedFitType || 'regular',
            stretchLevel: cleanProcessedResult.sizechart.stretch_level || extractedStretchLevel || 'moderate',
            materialInfo: materialInfo || '',
            showMeasurementDiagram: Boolean(showMeasurementDiagram),
            // Add chart name for easier identification
            chartName: chartName || `${productCategory} Size Chart`,
            // Add unit information
            unit: cleanProcessedResult.unit || 'in'
          };
          
          const chartsRef = collection(db, `shops/${shopId}/generated_sizecharts`);
          await addDoc(chartsRef, chartData);
        }
        
        console.log(`Saved ${results.length} charts to Firestore`);
      } catch (saveError) {
        console.error('Error saving charts to Firestore:', saveError);
        // Show an error to the user so they know something went wrong
        setError(`Charts were generated but couldn't be saved: ${saveError.message}. Please try again or contact support.`);
      }
      
    } catch (err) {
      console.error('Error generating size chart:', err);
      setError(err.message || 'Failed to generate size chart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Prepare country options for select dropdown
  const countryOptions = availableCountries.map(country => ({
    label: country.label,
    value: country.value
  }));

  // Prepare template options for select dropdown
  const templateOptions = [
    { label: 'No template', value: '' },
    ...templates.map(template => ({
      label: template.name || 'Unnamed Template',
      value: template.id
    }))
  ];

  // Prepare model options for select dropdown
  const modelOptions = aiModels
    .filter(model => {
      if (inputType === 'text') {
        // If no type is specified, assume it's a text model
        return !model.type || model.type === 'text';
      } else { // image
        return model.type === 'vision';
      }
    })
    .map(model => ({
      label: model.type === 'vision' ? `${model.displayName} (Vi)` : model.displayName,
      value: model.id
    }));
    
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <Text variant="headingMd">Generate Product Size Chart</Text>
          <Text variant="bodyMd" color="subdued">Create a size chart for your product by providing product details or an image link.</Text>
        </div>
        <Button
          icon={<Icon source={SaveMinor} />}
          onClick={saveCurrentSettings}
          loading={isSavingSettings}
          disabled={isSavingSettings}
          tooltip="Save these settings as default"
        />
      </div>
      
      {/* Product Information Section */}
      <div style={{ marginBottom: '24px' }}>
        <Text variant="headingMd" as="h3">Product Information</Text>
        <div style={{ marginTop: '12px' }}>
          <FormLayout>
            <TextField
              label="Product Category"
              value={productCategory}
              onChange={setProductCategory}
              placeholder="e.g., Women's Dress, Men's Shoes"
              helpText="Enter the specific product category"
              autoComplete="off"
            />
            
            <TextField
              label="Response Language"
              value={responseLanguage}
              onChange={setResponseLanguage}
              placeholder="e.g., English, French, German, etc."
              helpText="Enter the language for the generated size chart"
              autoComplete="off"
            />
            
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Text variant="headingMd">Size Chart Options</Text>
              <div style={{ marginTop: '8px' }}>
                <Checkbox
                  label="Include base sizes from input (S/M/L or original sizes)"
                  checked={includeBaseSizes}
                  onChange={(checked) => setIncludeBaseSizes(checked)}
                  helpText="When enabled, the AI will include both the target country sizes and any base sizes from your input"
                />
              </div>
            </div>
          </FormLayout>
        </div>
      </div>
      
      {/* Garment Template Section */}
      <div style={{ marginBottom: '24px' }}>
        <Text variant="headingMd" as="h3">Garment Fit & Measurement</Text>
        <div style={{ marginTop: '12px' }}>
          <FormLayout>
            {/* Template Selection */}
            <Select
              label="Template"
              options={templateOptions}
              value={selectedTemplateId}
              onChange={handleTemplateChange}
              disabled={isLoadingTemplates}
              loading={isLoadingTemplates}
              helpText="Select a template to apply pre-defined settings for your size chart"
            />
            
            {selectedTemplate && (
              <Banner status="info">
                <p>Using template: <strong>{selectedTemplate.name}</strong></p>
                <p>Template settings will be applied to the generated size chart.</p>
              </Banner>
            )}
            
            <Select
              label="Garment Type"
              options={garmentTypes.map(type => ({ label: type.label, value: type.value }))}
              value={garmentType}
              onChange={setGarmentType}
              helpText="Select the type of garment for appropriate measurement points"
            />
            
            <TextField
              label="Material Information"
              value={materialInfo}
              onChange={setMaterialInfo}
              placeholder="e.g., 95% Cotton, 5% Elastane"
              helpText="Enter fabric composition - AI will analyze to determine stretch level"
              autoComplete="off"
              multiline={2}
            />
            
            <Checkbox
              label="Show measurement diagram in size chart"
              checked={showMeasurementDiagram}
              onChange={(checked) => setShowMeasurementDiagram(checked)}
              helpText="Include a visual diagram showing how to measure the garment"
            />
            
            {/* Information about AI analysis */}
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
              <Text variant="headingSm">AI Analysis</Text>
              <div style={{ marginTop: '8px' }}>
                <Text variant="bodyMd">
                  The AI will automatically analyze your product details and material information to determine:
                </Text>
                <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li><Text variant="bodyMd"><strong>Fit Type</strong> (slim, regular, loose) based on product description</Text></li>
                  <li><Text variant="bodyMd"><strong>Stretch Level</strong> (none, slight, moderate, high) based on material composition</Text></li>
                </ul>
              </div>
              
              {/* No preview needed since the AI will determine the fit and stretch */}
            </div>
          </FormLayout>
        </div>
      </div>
      
      {/* AI Model Section */}
      <div style={{ marginBottom: '24px' }}>
        <Text variant="headingMd" as="h3">AI Model & Input Type</Text>
        <div style={{ marginTop: '12px' }}>
          <FormLayout>
            <div>
              <Select
                label="AI Model"
                options={modelOptions}
                value={selectedModelId}
                onChange={setSelectedModelId}
                disabled={aiModels.length === 0}
                error={aiModels.length === 0 ? 'No AI models available' : undefined}
              />
              <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Badge status="info">
                  {creditCost} {creditCost === 1 ? 'credit' : 'credits'}
                </Badge>
                <Text variant="bodySm" color="subdued">
                  {inputType === 'text' ? 'Text models' : 'Vision models'} are automatically filtered
                </Text>
              </div>
            </div>
            
            <ChoiceList
              title="Select Input Type"
              choices={[
                {label: 'Text Input (paste product details)', value: 'text'},
                {label: 'Image Input (URL to product image)', value: 'image'}
              ]}
              selected={[inputType]}
              onChange={value => setInputType(value[0])}
            />
          </FormLayout>
        </div>
      </div>
      
      {/* Input Section */}
      <div style={{ marginBottom: '24px' }}>
        <Text variant="headingMd" as="h3">Product Details</Text>
        <div style={{ marginTop: '12px' }}>
          <FormLayout>
            <TextField
              label="Chart Name"
              value={chartName}
              onChange={setChartName}
              placeholder="e.g., Women's Jeans Size Chart"
              helpText="Enter a descriptive name for your size chart"
              autoComplete="off"
            />
            {inputType === 'text' ? (
              <TextField
                label="Product Details"
                value={inputText}
                onChange={setInputText}
                multiline={6}
                placeholder="Paste product details, specifications, or any text that describes the product sizes"
                helpText="The AI will extract size information from this text"
              />
            ) : (
              <TextField
                label="Product Image URL"
                value={imageUrl}
                onChange={setImageUrl}
                placeholder="https://example.com/product-image.jpg"
                helpText="Enter the URL of an image showing the product or its size chart"
              />
            )}
          </FormLayout>
        </div>
      </div>
      
      {/* Target Markets Section */}
      <div style={{ marginBottom: '24px' }}>
        <Text variant="headingMd" as="h3">Target Markets</Text>
        <div style={{ marginTop: '12px' }}>
          <FormLayout>
            <ChoiceList
              allowMultiple
              title="Select Target Markets"
              choices={countryOptions}
              selected={selectedCountries}
              onChange={handleCountryChange}
              helpText="Select which markets you want to generate size charts for"
            />
            
            <Banner status="info">
              <p>You can manage your shop's supported countries in the Settings tab.</p>
            </Banner>
            
            {error && (
              <Banner status="critical">
                <p>{error}</p>
              </Banner>
            )}
            
            <Button
              primary
              size="large"
              onClick={handleSubmit}
              loading={loading}
              disabled={loading || !productCategory || !selectedModelId || selectedCountries.length === 0 || 
                      (inputType === 'text' && !inputText) || 
                      (inputType === 'image' && !imageUrl)}
            >
              Generate Size Chart
            </Button>
          </FormLayout>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div style={{ margin: '24px 0' }}>
          <LegacyStack distribution="center" spacing="loose">
            <Spinner size="large" />
            <Text variant="headingMd">Generating size chart...</Text>
          </LegacyStack>
        </div>
      )}
      
      {/* Results Section */}
      {result && result.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <Banner status="success" title="Size Charts Generated">
            <p>Your size charts have been successfully generated for {result.length} {result.length === 1 ? 'country' : 'countries'}!</p>
          </Banner>
          
          {result.map((countryResult, index) => (
            <div key={countryResult.country} style={{ marginTop: '20px', marginBottom: '30px' }}>
              <Text variant="headingMd">Size Chart for {countryResult.country}</Text>
              
              {countryResult.sizechart && (
                <div style={{ marginTop: '10px' }}>
                  <Text variant="bodyMd">
                    <strong>Name:</strong> {countryResult.sizechart.name}<br />
                    <strong>Category:</strong> {countryResult.sizechart.category}<br />
                    <strong>Unit:</strong> {countryResult.unit || 'Not specified'}
                  </Text>
                  
                  {/* Fit & Stretch Guide - Simplified */}
                  <div style={{ marginTop: '20px', marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    {/* Single FitGuideVisualizer with both fit and stretch */}
                    <FitGuideVisualizer 
                      fitType={fitType} 
                      stretchLevel={materialInfo ? analyzeStretchLevel(materialInfo) : stretchLevel}
                      fontFamily="sans-serif"
                      showLabels={true}
                      dotColor="#000000"
                      trackColor="#e6e6e6"
                      labelColor="#637381"
                      indicatorShape="dot"
                      indicatorSize={16}
                      indicatorOffset={0}
                    />
                  </div>
                  
                  {/* Size Chart Table */}
                  {countryResult.sizechart.columns && countryResult.sizechart.rows && (
                    <div style={{ overflowX: 'auto', marginTop: '10px' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid #ddd' }}>
                        <thead>
                          <tr>
                            {countryResult.sizechart.columns.map((col, colIndex) => (
                              <th key={colIndex} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                                {col.name} {col.unit !== 'none' ? `(${col.unit})` : ''}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {countryResult.sizechart.rows.map((row, rowIndex) => {
                            // Check if we're using the new format with original_size and country_size
                            const isNewFormat = row.hasOwnProperty('original_size') && row.hasOwnProperty('country_size');
                            
                            return (
                              <tr key={rowIndex}>
                                {isNewFormat ? (
                                  // New format with original_size and country_size
                                  <>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.original_size}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.country_size}</td>
                                    {row.values && row.values.map((value, valueIndex) => (
                                      <td key={valueIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>{value}</td>
                                    ))}
                                  </>
                                ) : (
                                  // Old format with just size
                                  <>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.size}</td>
                                    {row.values && row.values.map((value, valueIndex) => (
                                      <td key={valueIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>{value}</td>
                                    ))}
                                  </>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Measurement Diagram */}
                  {showMeasurementDiagram && (
                    <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                      <Text variant="headingMd" as="h4" style={{ marginBottom: '12px' }}>How to Measure the Product's Size?</Text>
                      <MeasurementDiagram garmentType={garmentType} fontFamily="sans-serif" />
                    </div>
                  )}
                  
                  {/* Size-Specific Fit Information */}
                  {countryResult.sizechart.fit_info && countryResult.sizechart.fit_info.length > 0 && (
                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <Text variant="headingMd" as="h4" style={{ marginBottom: '12px' }}>Size-Specific Fit Guide</Text>
                      <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '4px' }}>
                        {countryResult.sizechart.fit_info.map((fitItem, index) => {
                          // Determine the size to display
                          let sizeDisplay = '';
                          
                          // Check for original_size and country_size first (new format)
                          if (fitItem.original_size && fitItem.country_size) {
                            sizeDisplay = `${fitItem.original_size} (${fitItem.country_size})`;
                          } 
                          // Check for original_size only
                          else if (fitItem.original_size) {
                            sizeDisplay = fitItem.original_size;
                          }
                          // Try country_size if original_size is not available
                          else if (fitItem.country_size) {
                            sizeDisplay = fitItem.country_size;
                          }
                          // Fall back to just size if available
                          else if (fitItem.size) {
                            sizeDisplay = fitItem.size;
                          }
                          // Last resort: use index
                          else {
                            sizeDisplay = `Size ${index + 1}`;
                          }
                          
                          return (
                            <div key={index} style={{ 
                              marginBottom: index < countryResult.sizechart.fit_info.length - 1 ? '16px' : '0',
                              backgroundColor: 'white',
                              borderLeft: '4px solid #008060',
                              padding: '12px',
                              borderRadius: '0 4px 4px 0'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text variant="headingSm">{sizeDisplay}</Text>
                                {fitItem.fit_tag && (
                                  <span style={{ 
                                    backgroundColor: '#e4f2ed', 
                                    color: '#008060', 
                                    padding: '2px 8px', 
                                    borderRadius: '12px', 
                                    fontSize: '13px',
                                    fontWeight: 'medium'
                                  }}>
                                    {fitItem.fit_tag}
                                  </span>
                                )}
                              </div>
                              {fitItem.fit_description && (
                                <Text variant="bodyMd" style={{ marginTop: '8px', fontWeight: 'medium' }}>{fitItem.fit_description}</Text>
                              )}
                              {fitItem.buyer_hint && (
                                <Text variant="bodyMd" style={{ marginTop: '6px', fontStyle: 'italic' }}>{fitItem.buyer_hint}</Text>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {countryResult.notes && countryResult.notes.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <Text variant="bodyMd"><strong>Notes:</strong></Text>
                  <ul style={{ marginTop: '5px' }}>
                    {countryResult.notes.map((note, noteIndex) => (
                      <li key={noteIndex}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {index < result.length - 1 && <div style={{ margin: '20px 0', borderBottom: '1px solid #ddd' }}></div>}
            </div>
          ))}
          
          <details style={{ marginTop: '20px' }}>
            <summary><Text variant="bodyMd">View Raw JSON Data</Text></summary>
            <pre style={{ whiteSpace: 'pre-wrap', background: '#f4f6f8', padding: '15px', borderRadius: '4px', marginTop: '10px' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </>
  );
}
